import React, {useMemo, useRef} from "react"
import {useSelector} from "react-redux"
import PhoneInput from "react-phone-input-2"
import es from "react-phone-input-2/lang/es.json"
import "react-phone-input-2/lib/material.css"
import "./input/style.css"
import "./phoneField.css"
import {PHONE_COUNTRIES} from "../constants/phonePrefixes"
import {RootState} from "../globals"

// Plantel de Torreón (tb_campuses.id): ahí la clave lada es de tres dígitos.
const PLANTEL_TORREON = 2

// Teléfono con código de país (bandera + buscador de país + número).
//
// Por qué existe (2026-09-11): los teléfonos se guardaban como los tecleaba la
// cajera ("94209714", "14238318203", "+17027215769") y los envíos de WhatsApp
// asumían que todo número de un plantel hondureño era hondureño. Los alumnos
// con número de EE. UU. o México nunca recibieron sus comprobantes; les
// llegaron a desconocidos. Con este campo el número se guarda SIEMPRE como
// dígitos con código de país (50494209714, 14238318203): el backend lo sigue
// aceptando como entero y cualquier sistema sabe a qué país marcar.
//
// Orden del selector: 1) país del plantel, 2) Estados Unidos, 3) resto (con
// buscador). Usa react-phone-input-2, que ya estaba en el proyecto con el CSS
// de subrayado del formulario (src/components/input/style.css).

const soloDigitos = (v: any) => String(v ?? "").replace(/\D/g, "")

// ct_countries.id -> ISO2 de los países donde hay planteles.
const ISO2_POR_PAIS: {[id: number]: string} = {1: "mx", 2: "hn", 60: "sv", 66: "us", 79: "gt", 37: "ca"}

export function prefixForCountryId(countryId?: number): string
{
	return PHONE_COUNTRIES.find(c => c.id === countryId)?.prefix || "504"
}

// Códigos de país que de verdad aparecen en el registro: los planteles, Estados
// Unidos y Canadá, el resto de Centroamérica y los países donde hay alumnos que
// se fueron. Se limita a propósito (Esteban, 2026-09-11): con la lista completa,
// un número mexicano mal capturado de 11 dígitos como 81211243024 "empezaba con
// 81" y se mostraba como japonés. Lo que no cae en esta lista se muestra tal
// como está guardado, que es la señal de que hay que corregirlo.
const PREFIJOS_CONOCIDOS = [
	"1", "52", "503", "504", "502", "505", "506", "507",
	"34", "39", "33", "44", "49", "51", "54", "55", "56", "57", "58", "591", "593"
]

// Un número al que se le pegó el código de país más de una vez ("52" "52" "52" y luego el
// número) se muestra con un solo código. Solo se quita mientras quede un número completo
// detrás, para no mutilar uno bueno (Esteban, 2026-09-12).
function sinCodigoRepetido(local: string, prefix: string): string
{
	let limpio = local

	while(limpio.startsWith(prefix) && limpio.length - prefix.length >= 9)
	{
		limpio = limpio.slice(prefix.length)
	}

	return limpio
}

// Separa "50494209714" en {prefix:"504", local:"94209714"}. Un número sin
// código (8 dígitos en HN, 10 en MX) se atribuye al país por defecto.
export function splitPhone(value: any, defaultPrefix: string): {prefix: string, local: string}
{
	const d = soloDigitos(value)
	if(!d) return {prefix: defaultPrefix, local: ""}
	const esLocal = d.length <= 10 && !(d.startsWith(defaultPrefix) && d.length - defaultPrefix.length >= 7)
	if(esLocal) return {prefix: defaultPrefix, local: d}
	//El país del plantel manda: si el número empieza con su código, es de aquí.
	if(d.startsWith(defaultPrefix) && d.length - defaultPrefix.length >= 7)
	{
		return {prefix: defaultPrefix, local: sinCodigoRepetido(d.slice(defaultPrefix.length), defaultPrefix)}
	}
	const prefixes = Array.from(new Set(PREFIJOS_CONOCIDOS)).sort((a, b) => b.length - a.length)
	for(const p of prefixes)
	{
		const rest = d.length - p.length
		if(d.startsWith(p) && rest >= 7 && rest <= 12) return {prefix: p, local: sinCodigoRepetido(d.slice(p.length), p)}
	}
	return {prefix: defaultPrefix, local: d}
}

// Valor completo con código, para que la librería reconozca el país aunque el
// registro viejo traiga sólo el número local.
export function normalizePhone(value: any, defaultPrefix: string): string
{
	const {prefix, local} = splitPhone(value, defaultPrefix)
	return local ? `${prefix}${local}` : ""
}

// Agrupa el número como lo escribe la gente de cada país: con guion los de ocho
// dígitos (9995-2923 en Honduras, El Salvador y Guatemala) y con espacio los demás
// (81 1234 5678 en México, 865 385 2163 en EE. UU.).
function agruparNumero(prefix: string, local: string): string
{
	if(prefix === "52" && local.length === 11 && local.startsWith("1"))
	{
		//México se guarda en formato WhatsApp (52 + 1 + 10 dígitos); el 1 no se muestra.
		local = local.slice(1)
	}

	if(local.length === 8)
	{
		return local.replace(/(\d{4})(\d{4})/, "$1-$2")
	}

	if(local.length === 9)
	{
		return local.replace(/(\d{3})(\d{3})(\d{3})/, "$1 $2 $3")
	}

	if(local.length === 10)
	{
		if(prefix === "52")
		{
			//En México la clave es de dos dígitos solo en Ciudad de México, Guadalajara y
			//Monterrey; en el resto, Torreón incluido, es de tres: 871 123 4567 (Esteban,
			//2026-09-11).
			return /^(55|56|33|81)/.test(local)
				? local.replace(/(\d{2})(\d{4})(\d{4})/, "$1 $2 $3")
				: local.replace(/(\d{3})(\d{3})(\d{4})/, "$1 $2 $3")
		}

		return local.replace(/(\d{3})(\d{3})(\d{4})/, "$1 $2 $3")
	}

	return local
}

// Cómo se ve un teléfono en pantalla (Esteban, 2026-09-11): el número del país del
// plantel se muestra como lo escribe la gente de ahí, sin el código; sólo los de otro
// país llevan su prefijo adelante, que es justo lo que hace falta saber de ellos.
export function formatPhoneDisplay(value: any, defaultPrefix: string): string
{
	const {prefix, local} = splitPhone(value, defaultPrefix)

	if(!local)
	{
		return ""
	}

	const agrupado = agruparNumero(prefix, local)

	return prefix === defaultPrefix ? agrupado : `+${prefix} ${agrupado}`
}

interface Props
{
	id              : string
	name            : string
	value           : any
	onChange        : (value: string) => void
	campusCountryId?: number
	disabled?       : boolean
	required?       : boolean
}

const PhoneField = (props: Props) =>
{
	const iso2          = ISO2_POR_PAIS[props.campusCountryId || 0] || "hn"
	const defaultPrefix = prefixForCountryId(props.campusCountryId)
	const preferred     = useMemo(() => Array.from(new Set([iso2, "us"])), [iso2])
	// Mientras la persona escribe, la librería ya entrega el número CON código
	// (+52 → "525..."); si lo volviéramos a normalizar en cada render, el código
	// se antepondría una y otra vez ("52 52 52 5", bug del 2026-09-11). Sólo se
	// normaliza el valor que llega de afuera (registro guardado, campo vacío);
	// lo que nosotros mismos emitimos se pasa tal cual.
	/*
		Al teclear, el número de México se agrupa como se muestra en Estudiantes (Esteban,
		2026-09-23): Monterrey "81 1234 5678", Torreón "871 123 4567". La librería agrupaba
		todos igual ("811 234 5678"). Su máscara es fija por país y se lee una sola vez, así
		que se escoge por el plantel en uso; el número que se guarda no cambia.
	*/
	const currentUserCampus: any = useSelector((state: RootState) => state.currentUserCampus.value)
	const campusRef: any = useSelector((state: RootState) => state.campusRef.value)
	const plantelId: number = Number(campusRef?.[currentUserCampus]?.id || 0)
	const mascaraMx: string = plantelId === PLANTEL_TORREON ? "... ... ...." : ".. .... ...."
	const ultimoEmitido = useRef<string | null>(null)
	const entrante      = String(props.value ?? "")
	const value         = ultimoEmitido.current !== null && entrante === ultimoEmitido.current
		? entrante
		: normalizePhone(entrante, defaultPrefix)

	return <PhoneInput
	  containerClass="phoneField"
	  country={iso2}
	  preferredCountries={preferred}
	  key={"mx_" + mascaraMx}
	  masks={{hn: ".... ....", sv: ".... ....", gt: ".... ....", mx: mascaraMx}}   // HN/SV/GT se leen de 4 en 4 (Esteban, 2026-09-11)
	  preserveOrder={["preferredCountries"]}
	  value={value}
	  localization={es as any}
	  enableSearch
	  searchPlaceholder="Buscar país"
	  searchNotFound="Sin resultados"
	  countryCodeEditable={false}
	  disabled={props.disabled}
	  specialLabel=""
	  placeholder=""
	  inputProps={{id: props.id, name: props.name, required: props.required, minLength: 8}}
	  onChange={(v: string, country: any) =>
	  {
		  const d = soloDigitos(v)
		  // Sólo el código de país (el usuario cambió de país sin escribir número) = vacío.
		  const salida = d && d !== soloDigitos(country?.dialCode) ? d : ""
		  ultimoEmitido.current = salida
		  props.onChange(salida)
	  }}
	/>
}

export default PhoneField
